import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import NestedTable from "./NestedTable";
import Paginator from "./Paginator";
import Table from "./Table";
import TableContainer from "./TableContainer";
import ActiveFilters from "../filters/ActiveFilters";

import Icons from "@/components/icons";
import { Button } from "@/components/ui/v2/button";
import { ROUTES } from "@/constants/routes";
import { Filter } from "@/types/Filter";
import { TableColumn } from "@/types/table/tableProps";

interface DataTableProps<T, C = null> {
  activeFilters?: { [key: string]: string[] | number[] };
  allowDownload?: boolean;
  columns: Array<TableColumn<T>>;
  data: T[];
  direction: "asc" | "desc";
  editableFields?: Array<keyof T>;
  entityName: string;
  filters?: Filter;
  getNestedData?: (row: T) => C[];
  header?: string;
  idField: keyof T;
  isEditing?: boolean;
  isFetching: boolean;
  isLoading: boolean;
  itemsPerPage?: number;
  nestedColumns?: Array<TableColumn<C>>;
  newRoute?: string;
  onDownload?: () => void;
  onFilters?: () => void;
  onRowClicked?: (row: T) => void;
  onSort: (column: string) => void;
  onToggleRow?: (row: T, isExpanding: boolean) => void;
  onUpdateData?: (newData: T[]) => Promise<void>;
  openedRowId?: string | null;
  orderBy?: string;
  page: number;
  rightSideButton?: React.ReactNode;
  searchParams?: URLSearchParams;
  searchValue?: string;
  setItemsPerPage?: (itemsPerPage: number) => void;
  setPage: (page: number) => void;
  setSearch?: (search: string) => void;
  setSearchParams?: (params: URLSearchParams) => void;
  showAdd?: boolean;
  showFilters?: boolean;
  showSearch?: boolean;
  toggleFilter?: {
    checked: boolean;
    onChange: (checked: boolean) => void;
    label: string;
  };
  totalItems: number;
  isEditDisabled?: boolean;
}

const DataTable = <T extends object, C = null>({
  activeFilters = {},
  allowDownload = false,
  columns,
  data,
  direction,
  editableFields,
  entityName,
  filters,
  getNestedData,
  header,
  idField,
  isEditing,
  isFetching,
  isLoading,
  itemsPerPage = 10,
  nestedColumns,
  newRoute,
  onDownload,
  onFilters,
  onRowClicked,
  onSort,
  onToggleRow,
  onUpdateData,
  openedRowId,
  orderBy,
  page,
  rightSideButton,
  searchParams,
  searchValue,
  setItemsPerPage,
  setPage,
  setSearch,
  setSearchParams,
  showAdd = false,
  showFilters = false,
  showSearch = false,
  toggleFilter,
  totalItems,
  isEditDisabled = false,
}: DataTableProps<T, C>) => {
  const { t } = useTranslation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedData, setEditedData] = useState<T[]>(data);
  useEffect(() => {
    setEditedData(data);
  }, [data]);

  const isEditModeRef = useRef(isEditMode);
  useEffect(() => {
    isEditModeRef.current = isEditMode;
  }, [isEditMode]);

  // Cancel edit mode when itemsPerPage, page, searchValue, or data changes
  useEffect(() => {
    if (isEditModeRef.current) {
      setIsEditMode(false);
      setEditedData(data);
    }
  }, [itemsPerPage, page, searchValue, data]);

  const isNested = !!nestedColumns && !!getNestedData;

  const handleSaveChanges = async () => {
    if (onUpdateData) {
      await onUpdateData(editedData);
    }
    setIsEditMode(false);
  };

  const handleCancelEdit = () => {
    setEditedData(data);
    setIsEditMode(false);
  };

  const handleFieldChange = (
    rowIndex: number,
    field: keyof T,
    value: unknown
  ) => {
    const newData = [...editedData];
    newData[rowIndex] = {
      ...newData[rowIndex],
      [field]: value,
    };
    setEditedData(newData);
  };

  // Prepare buttons for the right side
  const getButtons = () => {
    if (isEditMode) {
      return [
        <Button
          key="cancel"
          variant="outline"
          onClick={handleCancelEdit}
        >
          <Icons.CrossIcon fontSize={24} />
          {t("common.cancel")}
        </Button>,
      ];
    }

    const buttons: React.ReactNode[] = [];

    if (allowDownload && onDownload) {
      buttons.push(
        <Button
          key="download"
          variant="outline"
          onClick={onDownload}
          disabled={data.length === 0}
        >
          <Icons.ArrowDownIcon fontSize={24} />
          {`${t("common.download")} ${t("common.csv")}`}
        </Button>
      );
    }

    // Edit button
    if (editableFields) {
      buttons.push(
        <Button
          key="edit"
          variant="outline"
          disabled={data.length === 0 || isEditDisabled}
          onClick={() => setIsEditMode(true)}
        >
          <Icons.EditSquareIcon fontSize={24} />
          {`${t("common.add")} ${t(
            `common.${entityName.toLocaleLowerCase()}`
          )}`}
        </Button>
      );
    } else if (rightSideButton) {
      buttons.push(rightSideButton);
    } else if (showAdd) {
      buttons.push(
        <Button key="add" asChild>
          <Link to={newRoute ?? ROUTES.new}>
            <Icons.AddIcon fontSize={24} />
            {`${t("common.add")} ${t(
              `common.${entityName.toLocaleLowerCase()}`
            )}`}
          </Link>
        </Button>
      );
    }

    return buttons.length > 0 ? buttons : undefined;
  };
  return (
    <TableContainer
      handleFilters={onFilters}
      header={header}
      onSearchChange={setSearch}
      rightSideButtons={getButtons()}
      searchValue={searchValue}
      showFilter={showFilters}
      showSearch={showSearch}
    >
      {((showFilters &&
        Object.values(activeFilters).some((filters) => filters.length > 0)) ||
        toggleFilter) && (
        <ActiveFilters
          addedFilters={Object.entries(activeFilters).flatMap(([key, values]) =>
            values.map((value) => {
              const filterKey = filters
                ? Object.keys(filters)?.find((filterKey) => filterKey === key)
                : null;
              const filter = filterKey
                ? filters?.[filterKey]?.find((filter) => filter.value === value)
                : null;

              return {
                key,
                label: t(`common.${key}`),
                value,
                valueLabel: filter ? filter.label : value.toString(),
              };
            })
          )}
          onRemoveFilter={(filter) => {
            const newParams = new URLSearchParams(searchParams);

            const paramKey = filter.key;
            const currentValues = newParams.getAll(paramKey);
            newParams.delete(paramKey);
            currentValues
              .filter((val) => val !== filter.value.toString())
              .forEach((val) => newParams.append(filter.key, val));

            if (newParams.get("page") !== "1") {
              newParams.set("page", "1");
            }

            setSearchParams?.(newParams);
          }}
          handleClearFilters={() => {
            const newParams = new URLSearchParams(searchParams);
            Object.keys(activeFilters).forEach((key) => newParams.delete(key));
            setSearchParams?.(newParams);
          }}
          toggleFilter={toggleFilter}
        />
      )}

      {isNested ? (
        <NestedTable<T, C>
          columns={columns}
          data={isEditMode ? editedData : data}
          direction={direction}
          getNestedData={getNestedData!}
          isLoading={isFetching}
          idField={idField}
          nestedColumns={nestedColumns!}
          onRowClicked={onRowClicked}
          onSort={onSort}
          onToggleRow={onToggleRow}
          openedRowId={openedRowId}
          orderBy={orderBy}
        />
      ) : (
        <Table
          columns={columns}
          data={isEditMode ? editedData : data}
          direction={direction}
          isLoading={isFetching}
          onRowClicked={!isEditMode ? onRowClicked : undefined}
          onSort={onSort}
          orderBy={orderBy}
          isEditMode={isEditMode}
          editableFields={editableFields}
          onFieldChange={handleFieldChange}
        />
      )}

      {isEditMode && (
        <div
          style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}
        >
          <div>
            <Button
              loading={isEditing}
              onClick={handleSaveChanges}
              disabled={isEditDisabled}
            >
              <Icons.CheckIcon fontSize={24} />
              {`${t("common.save")} ${t(
                `common.${entityName.toLocaleLowerCase()}`
              )}`}
            </Button>
          </div>
        </div>
      )}

      <Paginator
        page={page}
        isLoading={isLoading}
        itemsPerPage={itemsPerPage}
        setCurrentPage={setPage}
        setItemsPerPage={setItemsPerPage}
        totalItems={totalItems}
      />
    </TableContainer>
  );
};

export default DataTable;
